import { useCallback, useEffect, useRef, useState } from 'react';

export interface ReportChartDimensions {
  /** Layout width in CSS pixels. */
  width: number;
  /** Chart height in CSS pixels. */
  height: number;
}

const REFIT_DELAYS_MS = [0, 150, 400] as const;

/**
 * Measures a chart host using layout width so Recharts stays full width when a
 * parent uses CSS `zoom` for print-fit scaling (ResponsiveContainer reads visual
 * width and under-reports).
 * @param height - Fixed chart height in CSS pixels.
 * @returns Container ref and measured dimensions when width is known.
 */
export const useReportChartDimensions = (
  height: number,
): {
  containerRef: (node: HTMLDivElement | null) => void;
  dimensions: ReportChartDimensions | null;
} => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  const measure = useCallback(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }
    const nextWidth = element.offsetWidth;
    if (nextWidth > 0) {
      setWidth(current => (current === nextWidth ? current : nextWidth));
    }
  }, []);

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      elementRef.current = node;
      if (node) {
        measure();
      }
    },
    [measure],
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    measure();

    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(element);

    const onReflow = () => {
      measure();
    };
    window.addEventListener('resize', onReflow);
    window.addEventListener('beforeprint', onReflow);
    window.addEventListener('afterprint', onReflow);
    window.addEventListener('report-print-fit-complete', onReflow);

    const timeouts = REFIT_DELAYS_MS.map(delayMs =>
      window.setTimeout(measure, delayMs),
    );

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('beforeprint', onReflow);
      window.removeEventListener('afterprint', onReflow);
      window.removeEventListener('report-print-fit-complete', onReflow);
      timeouts.forEach(timeoutId => {
        window.clearTimeout(timeoutId);
      });
    };
  }, [measure]);

  return {
    containerRef,
    dimensions: width > 0 ? { width, height } : null,
  };
};
